import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, MessageCircle, Loader2, Leaf } from "lucide-react";
import { HEALTH_CONCERNS } from "@/data/content";
import { waLink, discoveryMessage } from "@/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  full_name: "",
  age: "",
  city: "",
  email: "",
  whatsapp: "",
  concern: "",
  goal: "",
  challenge: "",
  consent: false,
};

const Field = ({ label, required, error, children, htmlFor }) => (
  <div>
    <label htmlFor={htmlFor} className="block text-xs font-bold uppercase tracking-[0.12em] text-forest mb-1.5">
      {label} {required && <span className="text-peach">*</span>}
    </label>
    {children}
    {error && (
      <p role="alert" className="mt-1.5 text-xs font-semibold text-red-700" data-testid={`${htmlFor}-error`}>
        {error}
      </p>
    )}
  </div>
);

const inputCls =
  "w-full rounded-xl border border-botanical/20 bg-ivory px-4 py-3 text-sm text-forest placeholder:text-inkmuted/50 focus:border-sage focus:ring-2 focus:ring-sage/20 outline-none transition";

export default function DiscoveryModal({ open, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [waHref, setWaHref] = useState("");
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      window.__lenis?.stop();
    } else {
      document.body.style.overflow = "";
      window.__lenis?.start();
    }
    return () => {
      document.body.style.overflow = "";
      window.__lenis?.start();
    };
  }, [open]);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (form.full_name.trim().length < 2) er.full_name = "Please enter your full name.";
    const age = parseInt(form.age, 10);
    if (!age || age < 5 || age > 110) er.age = "Please enter a valid age.";
    if (form.city.trim().length < 2) er.city = "Please enter your city.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Please enter a valid email address.";
    if (form.whatsapp.replace(/\D/g, "").length < 8) er.whatsapp = "Please enter a valid WhatsApp number.";
    if (!form.concern) er.concern = "Please select your main health concern.";
    if (form.goal.trim().length < 2) er.goal = "Please share your biggest health goal.";
    if (!form.consent) er.consent = "Please confirm your understanding to continue.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setServerError("");
    if (!validate()) return;
    setSubmitting(true);
    const payload = {
      full_name: form.full_name.trim(),
      age: parseInt(form.age, 10),
      city: form.city.trim(),
      email: form.email.trim(),
      whatsapp: form.whatsapp.trim(),
      concern: form.concern,
      goal: form.goal.trim(),
      challenge: form.challenge.trim() || null,
      consent: form.consent,
    };
    try {
      const res = await fetch(`${API}/discovery-leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Submission failed");
      const link = waLink(
        discoveryMessage({ name: payload.full_name, concern: payload.concern, goal: payload.goal })
      );
      setWaHref(link);
      setSubmitted(true);
      window.open(link, "_blank", "noopener,noreferrer");
    } catch {
      setServerError("Something went wrong while submitting. Please try again — your details have not been saved yet.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    onClose();
    setTimeout(() => {
      setForm(EMPTY);
      setErrors({});
      setSubmitted(false);
      setServerError("");
    }, 400);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] bg-forest/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-6"
          onClick={close}
          role="dialog"
          aria-modal="true"
          aria-label="Book your free discovery call"
          data-testid="discovery-modal"
        >
          <motion.div
            initial={{ y: 90, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 90, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="bg-ivory w-full sm:max-w-2xl max-h-[94vh] sm:max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl shadow-hover"
          >
            <div className="sticky top-0 bg-ivory/95 backdrop-blur-md border-b border-botanical/10 px-6 sm:px-9 py-5 flex items-start justify-between gap-4 z-10">
              <div>
                <h2 className="font-serif text-2xl sm:text-3xl text-forest">Let's Understand Your Health Goals</h2>
                <p className="mt-1 text-sm text-inkmuted">
                  This short form helps us understand a little about you before your conversation with Dr. Anjali.
                </p>
              </div>
              <button
                onClick={close}
                aria-label="Close form"
                data-testid="discovery-modal-close-button"
                className="w-9 h-9 shrink-0 rounded-full bg-cardbg flex items-center justify-center text-forest hover:bg-sagesurface transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {!submitted ? (
              <form onSubmit={submit} noValidate className="px-6 sm:px-9 py-7 space-y-5" data-testid="discovery-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Full Name" required error={errors.full_name} htmlFor="full_name">
                    <input
                      id="full_name"
                      data-testid="discovery-input-name"
                      className={inputCls}
                      value={form.full_name}
                      onChange={set("full_name")}
                      placeholder="Your full name"
                      autoComplete="name"
                    />
                  </Field>
                  <Field label="Age" required error={errors.age} htmlFor="age">
                    <input
                      id="age"
                      data-testid="discovery-input-age"
                      type="number"
                      min="5"
                      max="110"
                      className={inputCls}
                      value={form.age}
                      onChange={set("age")}
                      placeholder="Your age"
                    />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="City" required error={errors.city} htmlFor="city">
                    <input
                      id="city"
                      data-testid="discovery-input-city"
                      className={inputCls}
                      value={form.city}
                      onChange={set("city")}
                      placeholder="Your city"
                      autoComplete="address-level2"
                    />
                  </Field>
                  <Field label="Email Address" required error={errors.email} htmlFor="email">
                    <input
                      id="email"
                      data-testid="discovery-input-email"
                      type="email"
                      className={inputCls}
                      value={form.email}
                      onChange={set("email")}
                      placeholder="you@example.com"
                      autoComplete="email"
                    />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="WhatsApp Number" required error={errors.whatsapp} htmlFor="whatsapp">
                    <input
                      id="whatsapp"
                      data-testid="discovery-input-whatsapp"
                      type="tel"
                      className={inputCls}
                      value={form.whatsapp}
                      onChange={set("whatsapp")}
                      placeholder="+91 XXXXX XXXXX"
                      autoComplete="tel"
                    />
                  </Field>
                  <Field label="Main Health Concern" required error={errors.concern} htmlFor="concern">
                    <select
                      id="concern"
                      data-testid="discovery-select-concern"
                      className={inputCls}
                      value={form.concern}
                      onChange={set("concern")}
                    >
                      <option value="">Select your main concern</option>
                      {HEALTH_CONCERNS.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
                <Field label="Biggest Health Goal" required error={errors.goal} htmlFor="goal">
                  <input
                    id="goal"
                    data-testid="discovery-input-goal"
                    className={inputCls}
                    value={form.goal}
                    onChange={set("goal")}
                    placeholder="e.g. I want sustainable weight management without extreme dieting"
                  />
                </Field>
                <Field label="Current Health Challenge (Optional)" error={errors.challenge} htmlFor="challenge">
                  <textarea
                    id="challenge"
                    data-testid="discovery-input-challenge"
                    rows={3}
                    className={`${inputCls} resize-none`}
                    value={form.challenge}
                    onChange={set("challenge")}
                    placeholder="Anything you'd like Dr. Anjali to know before your conversation..."
                  />
                </Field>
                <div>
                  <label className="flex items-start gap-3 cursor-pointer" htmlFor="consent">
                    <input
                      id="consent"
                      data-testid="discovery-input-consent"
                      type="checkbox"
                      checked={form.consent}
                      onChange={set("consent")}
                      className="mt-1 w-4 h-4 rounded accent-[#4F772D]"
                    />
                    <span className="text-xs leading-relaxed text-inkmuted">
                      I understand that this consultation is for health-coaching and wellness purposes and does
                      not replace medical diagnosis, treatment or professional medical advice.
                    </span>
                  </label>
                  {errors.consent && (
                    <p role="alert" className="mt-1.5 text-xs font-semibold text-red-700" data-testid="consent-error">
                      {errors.consent}
                    </p>
                  )}
                </div>
                {serverError && (
                  <p role="alert" className="text-sm font-semibold text-red-700" data-testid="discovery-server-error">
                    {serverError}
                  </p>
                )}
                <motion.button
                  whileHover={{ scale: submitting ? 1 : 1.02 }}
                  whileTap={{ scale: submitting ? 1 : 0.98 }}
                  type="submit"
                  disabled={submitting}
                  data-testid="discovery-form-submit-button"
                  className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-[#25D366] text-forest px-8 py-4 text-sm font-extrabold tracking-wide hover:brightness-95 transition-all disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={17} className="animate-spin" /> SUBMITTING...
                    </>
                  ) : (
                    <>
                      CONTINUE TO WHATSAPP <ArrowRight size={16} />
                    </>
                  )}
                </motion.button>
                <p className="text-center text-[11px] text-inkmuted/80">
                  Your details are private and used only to prepare for your Discovery Call.
                </p>
              </form>
            ) : (
              <div className="px-6 sm:px-9 py-14 text-center" data-testid="discovery-success">
                <motion.div
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className="w-16 h-16 rounded-full bg-sagesurface flex items-center justify-center mx-auto mb-6"
                >
                  <Leaf size={28} className="text-sage" />
                </motion.div>
                <h3 className="font-serif text-3xl text-forest">You're Almost There 🌿</h3>
                <p className="mt-4 text-sm sm:text-base text-inkmuted leading-relaxed max-w-md mx-auto">
                  Thank you for sharing your details. Please continue to WhatsApp so we can complete the next
                  step and arrange your free 1:1 Discovery Call with Dr. Anjali.
                </p>
                <motion.a
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.97 }}
                  href={waHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="success-continue-whatsapp-button"
                  className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-[#25D366] text-forest px-8 py-4 text-sm font-extrabold tracking-wide hover:brightness-95 transition-all"
                >
                  <MessageCircle size={17} /> CONTINUE TO WHATSAPP
                </motion.a>
                <p className="mt-5 text-xs text-inkmuted">
                  A pre-filled message is ready for you — just press send in WhatsApp.
                </p>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
